/**
 * RelationshipBrainSheet — Claims (Phase 3) + Stage (Phase 4) + Bio Updates
 * (Bio Update System Phase 1).
 *
 * Tabs are controlled when `value` + `onValueChange` are passed so the parent
 * (ErosCoach) can deep-link to a specific tab (e.g. when the user clicks the
 * Brain icon while a red-dot exploration is pending). Falls back to
 * `defaultValue` uncontrolled if either prop is omitted.
 *
 * The Bio Updates panel is personalised with `userPersonaName` and
 * `matchName` — column headers and empty-state copy use the real names, not
 * "user" or "match".
 */
import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import PerspectivePanel from "./PerspectivePanel";
import StageTimeline from "./StageTimeline";
import BioUpdatesPanel from "./BioUpdatesPanel";

export default function RelationshipBrainSheet({
  matchId,
  matchName,
  userPersonaId,
  userPersonaName,
  refreshKey = 0,
  coachSessionId = null,
  value,
  onValueChange,
}) {
  const controlled = typeof value === "string" && typeof onValueChange === "function";
  const tabsProps = controlled
    ? { value, onValueChange }
    : { defaultValue: "claims" };

  const [bioCount, setBioCount] = useState(0);

  return (
    <Tabs {...tabsProps} className="w-full" data-testid="brain-tabs">
      <TabsList className="w-full bg-divine-black/40 border border-divine-gold/20">
        <TabsTrigger
          value="claims"
          className="flex-1 data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold"
          data-testid="brain-tab-claims"
        >
          Claims
        </TabsTrigger>
        <TabsTrigger
          value="stage"
          className="flex-1 data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold"
          data-testid="brain-tab-stage"
        >
          Stage
        </TabsTrigger>
        <TabsTrigger
          value="bio"
          className="flex-1 data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold relative"
          data-testid="brain-tab-bio"
        >
          Bio Updates
          {bioCount > 0 && (
            <span
              className="ml-1.5 inline-flex items-center justify-center text-[10px] min-w-[18px] h-[18px] px-1 rounded-full bg-divine-gold text-divine-black font-semibold"
              data-testid="brain-tab-bio-badge"
            >
              {bioCount}
            </span>
          )}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="claims" className="mt-4">
        <PerspectivePanel matchId={matchId} refreshKey={refreshKey} coachSessionId={coachSessionId} />
      </TabsContent>
      <TabsContent value="stage" className="mt-4">
        <StageTimeline matchId={matchId} refreshKey={refreshKey} />
      </TabsContent>
      <TabsContent value="bio" className="mt-4">
        <BioUpdatesPanel
          matchId={matchId}
          matchName={matchName}
          userPersonaId={userPersonaId}
          userPersonaName={userPersonaName}
          refreshKey={refreshKey}
          onCountChange={setBioCount}
        />
      </TabsContent>
    </Tabs>
  );
}
